import { http } from "./http";
import type { FanOrderRow } from "../lib/fanRequestsModel";
import type {
  IntentResponse,
  RequestIntentBody,
  TipIntentBody,
  SetlistIntentBody,
  FreeSetlistResponse,
  BoostQuote,
  BoostIntentResponse,
  ConfirmIntentDetails,
  ConfirmIntentResponse,
  CashoutMethod,
  CashoutResponse,
  CashoutOptionsResponse,
  InstantPayoutStatus,
  PendingBalance,
  InstantCardResponse,
} from "../types/payments";

// Everything money: minting PaymentIntents, confirming them, and the talent's
// cashout. The server is the source of truth for every amount — these
// functions carry the fan's or talent's ask, never compute a charge.
export const paymentsApi = {
  createRequestIntent: (body: RequestIntentBody) => http.post<IntentResponse>("/payments/create-payment-intent", body),
  createTipIntent: (body: TipIntentBody) => http.post<IntentResponse>("/payments/create-tip-intent", body),
  createSetlistIntent: (body: SetlistIntentBody) => http.post<IntentResponse>("/payments/create-setlist-intent", body),
  /** A free playlist (amount 0) takes no card: the same endpoint records the order and, for a shelf item, delivers it. */
  claimFreeSetlist: (body: SetlistIntentBody & { amount: 0 }) => http.post<FreeSetlistResponse>("/payments/create-setlist-intent", body),
  createCounterIntent: (orderId: string) => http.post<IntentResponse>("/payments/create-counter-intent", { orderId }),
  boostQuote: (orderId: string, newAmount: number) => http.post<BoostQuote>("/payments/boost-quote", { orderId, newAmount }),
  boostIntent: (orderId: string, newAmount: number) => http.post<BoostIntentResponse>("/payments/boost-intent", { orderId, newAmount }),
  /** Tell the server the fan paid (the intent id when the client has it). A
      song request adds the fan's message and name (2026-10-10); only what
      the fan filled in travels, so a body without them is what it always was. */
  confirmIntent: (orderId: string, piId?: string, details: ConfirmIntentDetails = {}) => {
    const body: Record<string, string | boolean> = { orderId };
    if (piId) body.piId = piId;
    if (details.comment) body.comment = details.comment;
    if (details.clientNickname) body.clientNickname = details.clientNickname;
    if (details.clientInstagram) body.clientInstagram = details.clientInstagram;
    // Request Spotlight: only when the checkout showed the box (strict booleans).
    if (typeof details.spotlightAware === "boolean") body.spotlightAware = details.spotlightAware;
    if (typeof details.spotlightNameHidden === "boolean") body.spotlightNameHidden = details.spotlightNameHidden;
    return http.post<ConfirmIntentResponse>("/payments/confirm-intent", body);
  },
  /** Legacy Stripe Checkout return (/success?session_id=cs_…). */
  confirmCheckoutSession: (sessionId: string) => http.post<FanOrderRow>("/payments/confirm", { sessionId }),
  /** Local dev only — the server refuses it in production. */
  mockPay: (orderId: string) => http.post<unknown>("/payments/mock-pay", { orderId }),
  cashout: (method: CashoutMethod) => http.post<CashoutResponse>("/payments/cashout", { method }),
  cashoutOptions: (amountCents: number, currency: string) =>
    http.get<CashoutOptionsResponse>("/payments/cashout-options", { params: { amountCents, currency } }),
  instantPayoutStatus: () => http.get<InstantPayoutStatus>("/payments/instant-payouts"),
  /** Attach a debit card for instant payouts (a Stripe card token from the sheet). */
  addInstantCard: (token: string) => http.post<InstantCardResponse>("/payments/instant-payouts/card", { token }),
  pendingBalance: () => http.get<PendingBalance>("/payments/pending-balance"),
  dismissWelcomeBanner: () => http.post<unknown>("/payments/dismiss-welcome-banner"),
};
